import { useState } from 'react';

import { DealerData } from 'pages/Settings/LocalMarketSettings/api/useLocalMarket/useLocalMarket';
import { ColourOption } from 'pages/Settings/LocalMarketSettings/DealerSidebar/multiSelect.config';
import { CircleIconWithBorder } from 'pages/Settings/LocalMarketSettings/icons/CircleIcon';
import { StarIconWithBorder } from 'pages/Settings/LocalMarketSettings/icons/StarIcon';
import Button from '@lotlinx-aws/sparky-component-library/Sparky/Button';
import InteractiveRow from '@lotlinx-aws/sparky-component-library/Sparky/InteractiveRow';
import { COLOR } from '@lotlinx-aws/sparky-component-library/Sparky/styles/vars';

import styles from './DealerListItem.module.scss';
import Tooltip from '@lotlinx-aws/sparky-component-library/Sparky/Tooltip';

export interface DealerListItemProps {
  isKeyDealer: boolean;
  dealer: DealerData;
  setHighlightDealer: (cell: number | undefined) => void;
  addKeyDealer: (dealerId: number) => void;
  removeKeyDealer: (dealerId: number) => void;
  keyDealerList: ColourOption[];
  sidebarWidth: number;
}

/**
 * DealerListItem
 */
export default function DealerListItem({
  isKeyDealer,
  dealer,
  setHighlightDealer,
  addKeyDealer,
  removeKeyDealer,
  keyDealerList,
  sidebarWidth,
}: DealerListItemProps) {
  const [isHovered, setIsHovered] = useState(false);
  const isFranchise = dealer.dealerType === 'franchise';

  const availableWidth = sidebarWidth - 164;
  const labelTrimLength = Math.max(10, Math.floor(availableWidth / 8.56));

  const handleHightlight = (dealerId: number) => {
    setHighlightDealer(dealerId);
  };

  return (
    <Tooltip content={dealer.dealerName} disabled={dealer.dealerName.length < labelTrimLength}>
      <span
        onMouseEnter={() => {
          setIsHovered(true);
          handleHightlight(dealer.dealerId);
        }}
        onMouseLeave={() => {
          setIsHovered(false);
          setHighlightDealer(undefined);
        }}
      >
        <InteractiveRow
          label={dealer.dealerName}
          justifyFlexStart={true}
          labelTrimLength={labelTrimLength}
          customRightElement={
            isHovered ? (
              isKeyDealer ? (
                <Button
                  variant='text'
                  size='sm'
                  category='danger'
                  className={styles.highlightButton}
                  onClick={() => removeKeyDealer(dealer.dealerId)}
                  tooltip='Remove dealer from key competitors'
                  tooltipSide='right'
                >
                  Remove
                </Button>
              ) : (
                <Button
                  variant='text'
                  size='sm'
                  className={styles.highlightButton}
                  onClick={() => addKeyDealer(dealer.dealerId)}
                  disabled={keyDealerList.length > 4}
                  tooltip={
                    keyDealerList.length <= 4
                      ? 'Add dealer to key competitors'
                      : 'Max 5 selected. Remove one to add.'
                  }
                  tooltipSide='right'
                >
                  Add
                </Button>
              )
            ) : null
          }
          iconPrefix={
            isKeyDealer ? (
              <span className={styles.starIcon}>
                <StarIconWithBorder
                  color={isFranchise ? COLOR.SPARKY_PURPLE_400 : COLOR.SPARKY_YELLOW_500}
                  showShadow={false}
                />
              </span>
            ) : (
              <span className={styles.circleIcon}>
                <CircleIconWithBorder
                  color={
                    isFranchise
                      ? isHovered
                        ? COLOR.SPARKY_PURPLE_400
                        : `#A90DF866`
                      : isHovered
                        ? '#B09436'
                        : '#B0943666'
                  }
                  showShadow={false}
                />
              </span>
            )
          }
        ></InteractiveRow>
      </span>
    </Tooltip>
  );
}
